import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lightbulb } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface SkillsStepProps {
  data: string;
  onUpdate: (data: string) => void;
  onNext: () => void;
  onBack: () => void;
}

export const SkillsStep = ({ data, onUpdate, onNext, onBack }: SkillsStepProps) => {
  const [skillsText, setSkillsText] = useState(data || "");
  const { toast } = useToast();

  const handleChange = (value: string) => {
    setSkillsText(value);
    onUpdate(value);
  };

  const handleNext = () => {
    if (!skillsText.trim()) {
      toast({
        title: "Skills Required",
        description: "Please tell the employer why you're a good fit for this role.",
        variant: "destructive",
      });
      return;
    }

    if (skillsText.trim().split(/\s+/).length > 250) {
      toast({
        title: "Too Long",
        description: "Please keep your response under 250 words.",
        variant: "destructive",
      });
      return;
    }

    onNext();
  };

  const wordCount = skillsText.trim().split(/\s+/).filter(Boolean).length;

  return (
    <>
      <CardHeader>
        <CardTitle className="text-2xl">Why are you a good fit for this role?</CardTitle>
        <p className="text-muted-foreground">
          In a few sentences, tell the employer why you're interested in this job and what skills you can bring.
        </p>
      </CardHeader>

      <CardContent className="space-y-6 pb-8">
        <div className="space-y-2">
          <Textarea
            placeholder="Example: I'm interested in this warehouse role because I enjoy physical work and staying active. I have experience working in a team, meeting deadlines, and I'm a quick learner. I'm reliable and committed to doing quality work..."
            value={skillsText}
            onChange={(e) => handleChange(e.target.value)}
            rows={8}
            className="text-base resize-none"
          />
          <div className="flex justify-between text-sm">
            <p className="text-muted-foreground">
              {wordCount} / 250 words
            </p>
            {wordCount > 250 && (
              <p className="text-destructive font-medium">
                Please reduce word count
              </p>
            )}
          </div>
        </div>

        <div className="bg-accent/5 p-4 rounded-lg border border-accent/10">
          <div className="flex items-start gap-3">
            <Lightbulb className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-foreground mb-1">Tips for a great response:</p>
              <ul className="text-muted-foreground space-y-1 list-disc list-inside">
                <li>Mention specific skills relevant to the job</li>
                <li>Share why you're interested in this opportunity</li>
                <li>Be genuine and honest - employers appreciate authenticity</li>
                <li>Keep it concise and focused</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button variant="outline" onClick={onBack} className="flex-1">
            Back
          </Button>
          <Button onClick={handleNext} className="flex-1">
            Review Application
          </Button>
        </div>

        <div className="text-center">
          <button className="text-sm text-muted-foreground hover:text-foreground underline">
            Save and finish later
          </button>
        </div>
      </CardContent>
    </>
  );
};
